/**
 * PowerTrace — the Power (virtual dyno) panel.
 *
 * Thin wrapper over the generic channel-trace renderers that adds power-specific
 * chrome:
 *   • a "Vehicle" button (same editor as the top toolbar button)
 *   • an HP / kW unit toggle
 *   • a peak-power readout in the chosen unit (WOT, straight-line samples only
 *     when throttle / lateral G are logged; "*" marks an ungated peak)
 *   • a visible zero line (positive power vs braking)
 *
 * Like the generic channel windows, the panel can overlay or split out extra
 * logged channels. Power alone renders through the mature SingleChannelTrace;
 * once an extra channel is picked it hands over to MultiChannelTrace (per-signal
 * axes, Split, Same scale, colour mode). Both carry the draggable
 * positive/braking zero-split scale, persisted under one key. The selection
 * itself is App state (`channelPanelConfigs` under POWER_PANEL_KEY) so it
 * persists per tab like every other panel's.
 */
import { useState, useMemo } from 'react';
import { SingleChannelTrace } from './ChannelTrace';
import MultiChannelTrace from './MultiChannelTrace';
import { dynoPullSamples, robustEnvelopePeak } from '../../utils/powerPeak';

// Reserved `channelPanelConfigs` base name for the Power panel. A logged channel
// can never be called this, so it can't collide with a generic channel window.
export const POWER_PANEL_KEY = '__power__';

const HP_TO_KW = 0.745699;
const UNIT_KEY = 'power.unit';

export default function PowerTrace({
  lapTraces, selectedLaps,
  compareDrivers = [],
  customColors1 = null,
  anchoredTooltip = false,
  onEditVehicle = null,
  // Extra-channel plumbing, shaped like genericChannelTraceProps in App.jsx.
  additionalSignals = [],
  availableChannels = [],
  selectedAdditionalChannels = [],
  onAdditionalChannelToggle = null,
  splitMode = false,
  onSplitModeChange = null,
  sharedScale = false,
  onSharedScaleChange = null,
  colorMode = 'auto',
  onColorModeChange = null,
  unitPrefs = {},
  trackName = '',
  sessionDate = '',
  sessionVehicle = '',
  driverName1 = 'Driver 1',
  exportCompareDrivers = [],
}) {
  // Comparison drivers carry their power traces in `powerTraces`; remap onto the
  // `lapTraces` field ChannelTrace expects.
  const cmpForChannel = useMemo(
    () => compareDrivers.map(d => ({ ...d, lapTraces: d.powerTraces || {} })),
    [compareDrivers]
  );
  const [unit, setUnit] = useState(() => {
    try { return localStorage.getItem(UNIT_KEY) === 'kw' ? 'kw' : 'hp'; }
    catch { return 'hp'; }
  });
  const isKw = unit === 'kw';
  const valueTransform = useMemo(() => (isKw ? (v => v * HP_TO_KW) : undefined), [isKw]);

  const toggleUnit = (e) => {
    const next = e.target.checked ? 'kw' : 'hp';
    setUnit(next);
    try { localStorage.setItem(UNIT_KEY, next); } catch { /* ignore */ }
  };

  // Robust "peak power" (backend values are HP) aggregated across all selected
  // laps & both drivers — the dyno-envelope peak, not the single max sample.
  const { peakHp, peakGated, gradeSource } = useMemo(() => {
    const pts = [];
    const sources = new Set();
    let gated = true;
    // Gate per trace: the WOT threshold is a fraction of *that* lap's throttle
    // range, so a 0–100 logger and a 0–1 logger can share one readout.
    const collect = (map, laps) => (laps || []).forEach(idx => {
      const trace = map?.[idx];
      if (!trace?.points) return;
      if (trace.grade_source) sources.add(trace.grade_source);
      const positive = trace.points.filter(p => p.channel_value != null && p.channel_value > 0);
      if (!positive.length) return;
      const pull = dynoPullSamples(positive);
      if (!pull.gated) gated = false;
      pts.push(...pull.pts);
    });
    collect(lapTraces, selectedLaps);
    compareDrivers.forEach(d => collect(d.powerTraces, d.selectedLaps));
    if (!pts.length) return { peakHp: null, peakGated: false, gradeSource: null };
    return {
      peakHp: robustEnvelopePeak(pts),
      peakGated: gated,
      gradeSource: sources.size ? [...sources].join(' / ') : null,
    };
  }, [lapTraces, selectedLaps, compareDrivers]);

  const peakShown = peakHp == null ? null : Math.round(isKw ? peakHp * HP_TO_KW : peakHp);

  const headerExtra = (
    <>
      {peakShown != null && (
        <span
          style={{ fontSize: '0.72rem', color: '#9aa0b5', marginRight: 4 }}
          title={'Robust peak — the p98 of the power-vs-RPM envelope across the '
            + 'selected laps, not the single highest sample (which is a noisy '
            + 'transient). '
            + (peakGated
              ? 'Only full-throttle, straight-line samples count — a dyno pull, '
                + 'not a corner exit or a brake-zone transition.'
              : 'No throttle / lateral-G channel, so every positive sample counts.')
            + (gradeSource ? ` Road grade from ${gradeSource}.` : ' No road-grade correction.')}
        >
          peak {peakShown} {isKw ? 'kW' : 'hp'}{peakGated ? '' : ' *'}
        </span>
      )}
      <label
        style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: '0.72rem', color: '#9aa0b5', cursor: 'pointer' }}
        title="Show power in kilowatts"
      >
        <input type="checkbox" checked={isKw} onChange={toggleUnit} />
        kW
      </label>
      {onEditVehicle && (
        <button
          className="powerVehicleBtn"
          onClick={onEditVehicle}
          title="Edit vehicle parameters (mass, drag, …)"
          style={{
            background: 'none', border: '1px solid #3a3f55', color: '#9aa0b5',
            borderRadius: 4, padding: '1px 6px', fontSize: '0.72rem', cursor: 'pointer',
          }}
        >🏎 Vehicle</button>
      )}
    </>
  );

  const unitLabel = isKw ? 'kW' : 'hp';
  const exportProps = {
    signalName: 'Power',
    exportSlug: 'powertrace',
    trackName, sessionDate, sessionVehicle, driverName1, exportCompareDrivers,
  };

  if (additionalSignals.length > 0) {
    return (
      <MultiChannelTrace
        lapTraces={lapTraces} selectedLaps={selectedLaps}
        compareDrivers={cmpForChannel}
        customColors1={customColors1}
        title="Power"
        unit={unitLabel}
        units={[unitLabel]}
        yLabel={unitLabel}
        valueTransform={valueTransform}
        additionalSignals={additionalSignals}
        availableChannels={availableChannels}
        selectedAdditionalChannels={selectedAdditionalChannels}
        onAdditionalChannelToggle={onAdditionalChannelToggle}
        splitMode={splitMode}
        onSplitModeChange={onSplitModeChange}
        sharedScale={sharedScale}
        onSharedScaleChange={onSharedScaleChange}
        colorMode={colorMode}
        onColorModeChange={onColorModeChange}
        anchoredTooltip={anchoredTooltip}
        unitPrefs={unitPrefs}
        zeroLine
        zeroFracKey="power.zeroFrac"
        headerExtra={headerExtra}
        helpPanel="powertrace"
        {...exportProps}
      />
    );
  }

  return (
    <SingleChannelTrace
      lapTraces={lapTraces} selectedLaps={selectedLaps}
      compareDrivers={cmpForChannel}
      customColors1={customColors1}
      dataKey="channel_value" title="Power"
      yLabel={unitLabel}
      valueTransform={valueTransform}
      anchoredTooltip={anchoredTooltip}
      unitPrefs={unitPrefs}
      zeroLine
      zeroFracKey="power.zeroFrac"
      headerExtra={headerExtra}
      helpPanel="powertrace"
      availableChannels={availableChannels}
      selectedAdditionalChannels={selectedAdditionalChannels}
      onAdditionalChannelToggle={onAdditionalChannelToggle}
      {...exportProps}
    />
  );
}
